import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";

export function SmoothScroll({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);
  const location = useLocation();

  useEffect(() => {
    // Safety: always ensure scroll is enabled when mounting or route changes
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.touchAction = '';
    document.documentElement.style.overflow = '';
    
    // Skip on touch devices for native smooth scrolling
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // IMPORTANT: pages that use an internal scroll container (overflow-y-auto)
    // Lenis hijacks wheel scrolling and can prevent nested scroll from working.
    // So we disable Lenis on these routes.
    const isNestedScrollRoute =
      location.pathname.startsWith("/generate") || location.pathname.startsWith("/work") || location.pathname === "/features";

    if (isTouchDevice || isNestedScrollRoute) return;

    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => 1 - Math.pow(1 - t, 3),
      smoothWheel: true,
      wheelMultiplier: 0.8,
      infinite: false,
    });

    lenisRef.current = lenis;

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [location.pathname]);

  // Reset scroll on route change
  useEffect(() => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return <>{children}</>;
}
